/**
 * Générateur — Définition de la proportionnalité et expressions de la vie courante
 * Attendu ID : 682 | Niveau : 6ème | Domaine : Proportionnalité (cluster 41)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - definition_qcm          : QCM 4 choix — énoncer la définition
 *   - expressions_relier      : relier 4 paires expression ↔ grandeurs associées
 *   - proprietes_vf           : V/F 6 lignes (3V + 3F)
 *   - prix_unitaire_saisie    : saisie — calcul direct depuis « au kilo / par minute »
 *   - interpretation_qcm      : QCM 4 choix — décoder le sens d'une expression
 *   - vocabulaire_qcm         : QCM 4 choix — reconnaître une expression proportionnelle
 *
 * Spécificité : 100 % texte pur (pas de SVG). Les formats à pool borné
 * (définitions, relier) assument leur unicité réelle — plus de préfixe de
 * lettres artificiel dans les énoncés (décision « Question CMFKH » 15/07) ;
 * seuils surchargés dans coverage-targets si nécessaire.
 *
 * Partition avec attendus voisins :
 *   - 683 : identifier si une situation donnée relève du modèle (tableau, énoncé)
 *   - 684 : procédures de résolution (linéarité, retour à l'unité multi-étapes)
 *   - 685 : tableau de proportionnalité / notations symboliques
 *   - 686 : échelles
 *
 * Cible : 20/20 uniques sur 20 itérations, 5 runs consécutifs minimum.
 */

import {
    randomChoice, shuffle, randInt,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 682;

const FRUITS =['pommes', 'poires', 'oranges', 'bananes', 'kiwis', 'cerises', 'fraises', 'raisins'];
const PRIX_POOL = [0.80, 1.20, 1.50, 1.80, 2.00, 2.50, 3.00, 3.50, 4.00];
const MASSE_POOL = [2, 3, 4, 5, 6, 8, 10];
const DEBIT_POOL = [10, 12, 15, 20, 25];
const DUREE_MIN_POOL = [2, 3, 4, 5, 6];
const CONSO_POOL = [4, 5, 6, 7, 8];
const K_POOL = [1, 2, 3, 4, 5];
const SALAIRE_POOL = [12, 14, 15, 18, 20];
const HEURES_POOL = [2, 3, 4, 5, 6, 7, 8];
const VITESSE_POOL = [40, 50, 60, 70, 80, 90, 100];

const L_DEF    = 'Définition de la proportionnalité (QCM)';
const L_EXPR   = 'Expressions de la vie courante (relier)';
const L_VF     = 'Propriétés et contre-exemples (V/F)';
const L_PRIX   = 'Prix / consommation / débit unitaires (saisie)';
const L_INT    = 'Interprétation d\'une expression (QCM)';
const L_VOC    = 'Vocabulaire : expressions proportionnelles (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_DEF,
});


// Formatte un nombre décimal (remplace . par , pour l'affichage FR)
// en supprimant les zéros traînants inutiles (ex : 3.00 → 3 ; 3.50 → 3,5).
const fmt = (n) => {
    const s = Number(n).toFixed(2);
    const trimmed = s.replace(/\.?0+$/, '');
    return trimmed.replace('.', ',');
};

// ─── Format 1 — Définition (QCM) ─────────────────────────────

// Distracteurs en structure parallèle (revue 2026-08-09) : même squelette et
// même longueur que la bonne réponse, seule l'idée fausse change — avant,
// « coche la réponse la plus longue » gagnait dans 84,6 % des tirages.
const DEF_VARIANTS = [
    {
        question: 'Deux grandeurs sont proportionnelles lorsque…',
        bonne: '…on peut obtenir les mesures de l\'une en multipliant les mesures de l\'autre par un même nombre non nul.',
        distracteurs: [
            '…on peut obtenir les mesures de l\'une en ajoutant un même nombre fixé aux mesures de l\'autre.',
            '…on peut obtenir les mesures de l\'une en soustrayant un même nombre fixé aux mesures de l\'autre.',
            '…on peut obtenir les mesures de l\'une en multipliant celles de l\'autre par un nombre différent à chaque fois.',
            '…les deux grandeurs augmentent en même temps, sans qu\'aucun calcul précis ne relie leurs mesures entre elles.',
            '…les deux grandeurs ont toujours des mesures parfaitement égales, quelle que soit la situation étudiée.',
        ],
    },
    {
        question: 'On dit qu\'une grandeur est proportionnelle à une autre lorsque…',
        bonne: '…il existe un nombre non nul par lequel on multiplie les mesures de l\'une pour obtenir les mesures de l\'autre.',
        distracteurs: [
            '…il existe un nombre positif qu\'on ajoute aux mesures de l\'une pour obtenir toutes les mesures de l\'autre grandeur.',
            '…on multiplie les mesures de l\'une par un nombre qui change à chaque mesure pour obtenir celles de l\'autre.',
            '…les deux grandeurs augmentent en même temps, même si aucun nombre précis ne relie leurs mesures entre elles.',
            '…les deux grandeurs ont exactement la même mesure, à tous les moments de la situation que l\'on étudie.',
            '…on peut tracer l\'évolution des deux grandeurs sur un même graphique, quelle que soit la forme de la courbe obtenue.',
        ],
    },
    {
        question: 'Dans une situation de proportionnalité, pour passer des mesures d\'une grandeur à celles de l\'autre, on…',
        bonne: '…multiplie toujours par le même nombre (non nul).',
        distracteurs: [
            '…ajoute toujours un même nombre fixé (positif).',
            '…multiplie par un nombre différent à chaque nouvelle mesure.',
            '…soustrait toujours exactement le même nombre (non nul).',
            '…divise toujours par un nombre plus grand que 1.',
            '…applique parfois une multiplication, parfois une addition.',
        ],
    },
    {
        question: 'Un coefficient de proportionnalité est :',
        bonne: 'Un nombre non nul par lequel on multiplie les mesures d\'une grandeur pour obtenir les mesures de l\'autre.',
        distracteurs: [
            'Un nombre qui peut être nul et par lequel on multiplie les mesures d\'une grandeur pour obtenir celles de l\'autre.',
            'Un nombre qu\'on ajoute aux mesures d\'une grandeur pour obtenir toutes les mesures de l\'autre grandeur.',
            'Un nombre qui change à chaque nouvelle étape du calcul, selon la mesure de la grandeur de départ choisie.',
            'La différence entre les mesures des deux grandeurs, calculée à chaque nouvelle étape de la situation.',
            'Un nombre toujours plus petit que 1, par lequel on multiplie toutes les mesures de la première grandeur étudiée.',
        ],
    },
];

const genDefinitionQcm = () => {
    const variant = randomChoice(DEF_VARIANTS);
    const prefix = `<p>${variant.question}</p>`;

    // Tirer 3 distracteurs distincts parmi le pool
    const distracteurs = shuffle(variant.distracteurs).slice(0, 3);
    const choix = [variant.bonne, ...distracteurs];

    const payload = makeQcm(choix, variant.bonne, prefix, opts(L_DEF));
    payload._meta.generatorKey = 'definition_qcm';
    return payload;
};

// ─── Format 2 — Expressions (relier) ─────────────────────────

const EXPRESSIONS_POOL = [
    { source: 'Prix au kilogramme', target: 'prix et masse' },
    { source: 'Vitesse en km/h', target: 'distance et durée' },
    { source: 'Nombre de pages imprimées par minute', target: 'nombre de pages et durée' },
    { source: 'Consommation en litres aux 100 km', target: 'volume d\'essence et distance parcourue' },
    { source: 'Débit en litres par seconde', target: 'volume d\'eau et durée' },
    { source: 'Salaire horaire', target: 'salaire et durée de travail' },
    { source: 'Prix à l\'unité', target: 'prix et nombre d\'objets' },
    // Enrichissement (revue 2026-08-10) : C(7,4) = 35 → C(9,4) = 126.
    { source: 'Prix au litre', target: 'prix et volume' },
    { source: 'Masse de pâte par crêpe', target: 'masse de pâte et nombre de crêpes' },
];

// Cibles leurres du relier (revue 2026-08-09) : couples de grandeurs
// plausibles ne correspondant à aucune expression du pool — en 4 ↔ 4 fermé,
// la dernière paire était offerte.
const RELIER_LEURRES = [
    'masse et durée',
    'prix et distance parcourue',
    'nombre d\'objets et distance',
];

const genExpressionsRelier = () => {
    // C(7,4) = 35 combinaisons réelles — pool borné ASSUMÉ (plus de préfixe
    // de lettres artificiel : seuil d'unicité surchargé dans coverage-targets
    // si besoin, cf. décision « Question CMFKH » du 15/07).
    const pairs = shuffle(EXPRESSIONS_POOL).slice(0, 4);
    const leurres = shuffle(RELIER_LEURRES).slice(0, 2)
        .map(t => ({ target: t, code: 'grandeurs_non_associees' }));
    const texte = `<p>Pour chaque expression de la vie courante, indique les deux grandeurs qu'elle met en relation. `
        + `Attention : certaines propositions ne correspondent à aucune expression.</p>`;
    const payload = makeRelier(pairs, texte, { ...opts(L_EXPR), cibles_leurres: leurres });
    payload._meta.generatorKey = 'expressions_relier';
    return payload;
};

// ─── Format 3 — Propriétés (V/F) ─────────────────────────────

// Clés sémantiques (revue 2026-08-09) : jamais deux affirmations sur le même
// objet dans un tirage — la définition vraie (« en multipliant ») et sa
// version fausse (« en ajoutant ») tombaient ensemble, l'une donnant l'autre.
// Idem pour le coefficient (« non nul » vs « peut être nul »).
const BUILDERS_V = [
    { k: 'def', build: () => 'Deux grandeurs sont proportionnelles si on peut obtenir les mesures de l\'une en multipliant celles de l\'autre par un même nombre non nul.' },
    { k: 'prix_masse', build: () => 'Dire que le prix est proportionnel à la masse revient à dire qu\'il existe un prix au kilogramme fixe.' },
    { k: 'calc_fruit', build: ({ fruit, p, k }) => `Si 1 kg de ${fruit} coûte ${fmt(p)} €, alors ${k} kg de ${fruit} coûtent ${fmt(p * k)} €.` },
    { k: 'vitesse', build: () => 'Une vitesse en km/h traduit une proportionnalité entre la distance parcourue et la durée du trajet.' },
    { k: 'linearite', build: () => 'Dans une situation de proportionnalité, doubler l\'une des grandeurs revient à doubler l\'autre.' },
    { k: 'coeff', build: () => 'Un coefficient de proportionnalité est nécessairement non nul.' },
    { k: 'calc_pages', build: ({ d, t }) => `Une imprimante qui imprime ${d} pages par minute imprime ${d * t} pages en ${t} minutes.` },
    { k: 'calc_km', build: ({ v, h }) => `Un cycliste qui roule à la vitesse constante de ${v} km/h parcourt ${v * h} km en ${h} h.` },
];

const BUILDERS_F = [
    { k: 'def', build: () => 'Deux grandeurs sont proportionnelles si on peut obtenir les mesures de l\'une en ajoutant un même nombre aux mesures de l\'autre.' },
    { k: 'covariation', build: () => 'Toute situation où les deux grandeurs augmentent en même temps est proportionnelle.' },
    { k: 'age_taille', build: () => 'L\'âge d\'une personne et sa taille sont proportionnels tout au long de la vie.' },
    { k: 'coeff', build: () => 'Le coefficient de proportionnalité peut être égal à zéro.' },
    { k: 'age_ecart', build: ({ d }) => `Si Paul a ${d} ans de plus que Jules, alors l'âge de Paul et celui de Jules sont proportionnels.` },
    // Scission (revue 2026-08-09) : « le périmètre ET l'aire sont
    // proportionnels au côté » mêlait un vrai et un faux — l'élève qui savait
    // le périmètre proportionnel cochait Vrai en survolant le « et ».
    { k: 'aire_carre', build: () => 'L\'aire d\'un carré est proportionnelle à la longueur de son côté.' },
    { k: 'def', build: () => 'Dire que deux grandeurs sont proportionnelles, c\'est dire qu\'elles sont toujours égales.' },
    { k: 'covariation', build: () => 'Pour reconnaître une proportionnalité, il suffit de vérifier que les deux grandeurs varient.' },
];

const genProprietesVF = () => {
    const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';

    for (let attempt = 0; attempt < 6; attempt++) {
        const fruit = randomChoice(FRUITS);
        const p = randomChoice(PRIX_POOL);
        const k = randomChoice(MASSE_POOL);
        const d = randomChoice(DEBIT_POOL);
        const t = randomChoice(DUREE_MIN_POOL);
        const v = randomChoice(VITESSE_POOL);
        const h = randomChoice(HEURES_POOL);
        const dDiff = randInt(2, 9);
        // Deux contextes distincts : `d` = débit en ctxV (pages/min) vs `d` = écart d'âge en ctxF.
        const ctxV = { fruit, p, k, d, t, v, h };
        const ctxF = { d: dDiff };

        // Quota variable (revue 2026-08-09) : le 3 V / 3 F constant se
        // déduisait par comptage. Sélection à clés sémantiques distinctes,
        // vrais et faux confondus.
        const nbVrais = randInt(2, 4);
        const nbFaux = 6 - nbVrais;
        const seenKeys = new Set();
        const pick = (pool, n, ctx) => {
            const out = [];
            for (const b of shuffle(pool)) {
                if (out.length >= n) break;
                if (!seenKeys.has(b.k)) {
                    seenKeys.add(b.k);
                    out.push(b.build(ctx));
                }
            }
            return out;
        };
        const lignesV = pick(BUILDERS_V, nbVrais, ctxV);
        const lignesF = pick(BUILDERS_F, nbFaux, ctxF);
        if (lignesV.length < nbVrais || lignesF.length < nbFaux) continue;

        const combined = shuffle([
            ...lignesV.map(l => ({ ligne: l, br: 0 })),
            ...lignesF.map(l => ({ ligne: l, br: 1 })),
        ]);

        const lignes = combined.map(x => x.ligne);
        const bonnesReponses = combined.map(x => x.br);

        if (new Set(lignes).size === lignes.length) {
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'proprietes_vf';
            return payload;
        }
    }

    // Fallback déterministe : 3 V fixes + 3 F fixes, aucun doublon, clés
    // sémantiques toutes distinctes (revue 2026-08-09).
    const fallbackLignes = [
        'Deux grandeurs sont proportionnelles si on peut obtenir les mesures de l\'une en multipliant celles de l\'autre par un même nombre non nul.',
        'Une vitesse en km/h traduit une proportionnalité entre la distance parcourue et la durée du trajet.',
        'Un coefficient de proportionnalité est nécessairement non nul.',
        'Toute situation où les deux grandeurs augmentent en même temps est proportionnelle.',
        'L\'âge d\'une personne et sa taille sont proportionnels tout au long de la vie.',
        'L\'aire d\'un carré est proportionnelle à la longueur de son côté.',
    ];
    const fallbackBr = [0, 0, 0, 1, 1, 1];
    const order = shuffle([0, 1, 2, 3, 4, 5]);
    const lignes = order.map(i => fallbackLignes[i]);
    const bonnesReponses = order.map(i => fallbackBr[i]);
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'proprietes_vf';
    return payload;
};

// ─── Format 4 — Prix / consommation / débit (saisie) ─────────

const genPrixUnitaireSaisie = () => {
    const r = Math.random();
    let texte, reponse, suffixe;

    // Revue 2026-08-10 : accord de « vendus/vendues » (kiwis et raisins sont
    // masculins) et sujets introduits — « Elle imprime… », « Sa consommation »,
    // « Son salaire » démarraient sur un pronom sans antécédent.
    if (r < 0.25) {
        // V1 — prix au kilo × masse
        const fruit = randomChoice(FRUITS);
        const vendu = ['kiwis', 'raisins'].includes(fruit) ? 'vendus' : 'vendues';
        const p = randomChoice(PRIX_POOL);
        const m = randomChoice(MASSE_POOL);
        texte = `<p>Des ${fruit} sont ${vendu} à ${fmt(p)} € le kilogramme. Quel est le prix de ${m} kg de ${fruit} ?</p>`;
        reponse = fmt(p * m);
        suffixe = ' €';
    } else if (r < 0.5) {
        // V2 — pages/minute × durée
        const d = randomChoice(DEBIT_POOL);
        const t = randomChoice(DUREE_MIN_POOL);
        texte = `<p>Une imprimante imprime ${d} pages par minute. Combien de pages imprime-t-elle en ${t} minutes ?</p>`;
        reponse = d * t;
        suffixe = ' pages';
    } else if (r < 0.75) {
        // V3 — consommation aux 100 km
        const c = randomChoice(CONSO_POOL);
        const k = randomChoice(K_POOL);
        texte = `<p>Une voiture consomme ${c} L aux 100 km. Quelle est sa consommation pour ${k * 100} km ?</p>`;
        reponse = c * k;
        suffixe = ' L';
    } else {
        // V4 — salaire horaire
        const qui = randomChoice([
            { n: 'Karim', pr: 'il' }, { n: 'Léa', pr: 'elle' },
            { n: 'Adam', pr: 'il' }, { n: 'Manon', pr: 'elle' },
        ]);
        const s = randomChoice(SALAIRE_POOL);
        const h = randomChoice(HEURES_POOL);
        texte = `<p>Le salaire horaire de ${qui.n} est de ${s} €. Combien gagne-t-${qui.pr} pour ${h} heures de travail ?</p>`;
        reponse = s * h;
        suffixe = ' €';
    }

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_PRIX),
        strict: false,
        suffixe,
    });
    payload._meta.generatorKey = 'prix_unitaire_saisie';
    return payload;
};

// ─── Format 5 — Interprétation (QCM) ─────────────────────────

const genInterpretationQcm = () => {
    const r = Math.random();
    let question, bonne, choix;

    if (r < 0.25) {
        // V1 — conso L/100km
        const c = randomChoice(CONSO_POOL);
        question = `Que signifie "${c} L aux 100 km" pour ce véhicule ?`;
        bonne = `Pour 100 km parcourus, le véhicule consomme ${c} L d'essence.`;
        choix = [
            bonne,
            `Le véhicule consomme ${c} L par km.`,
            'Pour 1 L d\'essence, le véhicule parcourt 100 km.',
            `Le véhicule consomme 100 L tous les ${c} km.`,
        ];
    } else if (r < 0.5) {
        // V2 — vitesse km/h
        const v = randomChoice(VITESSE_POOL);
        question = `Que signifie "${v} km/h" pour ce véhicule ?`;
        bonne = `En 1 heure, le véhicule parcourt ${v} km.`;
        // « Pour v km, il faut exactement 60 minutes… » remplacé (revue
        // 2026-08-10) : à v km/h, c'est exactement VRAI — l'élève qui le
        // cochait avait mathématiquement raison.
        choix = [
            bonne,
            `Pour 1 km, le véhicule met ${v} heures.`,
            `Le véhicule roule ${v} heures par km.`,
            `En 1 minute, le véhicule parcourt ${v} km.`,
        ];
    } else if (r < 0.75) {
        // V3 — pages/min
        const d = randomChoice(DEBIT_POOL);
        question = `Que signifie "${d} pages/min" pour cette imprimante ?`;
        bonne = `En 1 minute, l'imprimante imprime ${d} pages.`;
        // « Il faut d pages pour 1 minute » remplacé (revue 2026-08-10) :
        // la tournure se lisait comme la bonne réponse.
        choix = [
            bonne,
            `Pour imprimer 1 page, il faut ${d} minutes.`,
            `L'imprimante met ${d} minutes par page.`,
            `En 1 seconde, l'imprimante imprime ${d} pages.`,
        ];
    } else {
        // V4 — prix au kilo
        const p = randomChoice(PRIX_POOL);
        const fruit = randomChoice(FRUITS);
        question = `Que signifie "${fmt(p)} € le kg" pour des ${fruit} ?`;
        bonne = `Pour 1 kg de ${fruit}, on paie ${fmt(p)} €.`;
        choix = [
            bonne,
            `Pour 1 € de ${fruit}, on a ${fmt(p)} kg.`,
            `Pour ${fmt(p)} kg de ${fruit}, on paie 1 €.`,
            `${fmt(p)} € permettent d'acheter 1 g de ${fruit}.`,
        ];
    }

    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_INT));
    payload._meta.generatorKey = 'interpretation_qcm';
    return payload;
};

// ─── Format 6 — Vocabulaire (QCM) ────────────────────────────

// Pools enrichis (revue 2026-08-10) : 7 → 10 et 6 → 8 expressions.
const PROP_EXPRESSIONS = [
    'Prix au kilogramme',
    'Vitesse en km/h',
    'Débit en litres par seconde',
    'Salaire horaire',
    'Nombre de pages imprimées par minute',
    'Consommation en litres aux 100 km',
    'Prix à l\'unité',
    'Prix au litre',
    'Distance parcourue par tour de roue',
    'Masse de pâte par crêpe',
];

// Réécritures (revue 2026-08-10) : « à heure fixée » portait deux lectures
// contradictoires (heure figée vs débit par heure) ; « selon qu'il est en
// promotion ou non » n'était pas une grandeur mesurable.
const NON_PROP_EXPRESSIONS = [
    'Âge et taille d\'une personne',
    'Température de la journée selon l\'heure',
    'Poids d\'un bébé en fonction de son âge',
    'Hauteur d\'eau dans une baignoire qui se remplit puis est vidée',
    'Nombre de voitures passant à un péage et durée écoulée',
    'Pointure d\'un élève et moyenne en mathématiques',
    'Note à un contrôle et temps passé devant la télévision',
    'Nombre de pages d\'un livre et âge de son auteur',
];

const construireVocabulaireQcm = () => {
    const r = Math.random();
    let question, bonne, choix;

    if (r < 0.25) {
        // V1 — identifier une expression proportionnelle
        bonne = randomChoice(PROP_EXPRESSIONS);
        const distract = shuffle(NON_PROP_EXPRESSIONS).slice(0, 3);
        choix = [bonne, ...distract];
        question = `Parmi ces 4 expressions, laquelle traduit une situation de proportionnalité ?`;
    } else if (r < 0.5) {
        // V2 — identifier l'expression NON proportionnelle
        bonne = randomChoice(NON_PROP_EXPRESSIONS);
        const distract = shuffle(PROP_EXPRESSIONS).slice(0, 3);
        choix = [bonne, ...distract];
        question = `Parmi ces 4 expressions, laquelle NE traduit PAS une situation de proportionnalité ?`;
    } else if (r < 0.75) {
        // V3 — conséquence de la proportionnalité
        question = `Deux grandeurs sont proportionnelles. Laquelle de ces affirmations est correcte ?`;
        bonne = 'Si on multiplie l\'une par 3, l\'autre est aussi multipliée par 3.';
        choix = [
            bonne,
            'Si on ajoute 3 à l\'une, on ajoute 3 à l\'autre.',
            'Les deux grandeurs sont toujours égales.',
            'Elles augmentent toutes les deux, mais pas forcément dans le même rapport.',
        ];
    } else {
        // V4 — nature du coefficient
        question = `Un coefficient de proportionnalité peut être :`;
        bonne = 'Un nombre non nul, entier ou décimal.';
        choix = [
            bonne,
            'Zéro exactement.',
            'Uniquement un nombre entier.',
            'Un nombre qui change à chaque calcul.',
        ];
    }

    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_VOC));
    payload._meta.generatorKey = 'vocabulaire_qcm';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-10, recette gen 679) : mémorise la
// signature de la dernière question servie et re-tire si le nouveau tirage
// est identique — plus jamais deux fois la même question d'affilée.
let derniereSignatureVocab = null;
const signatureVocab = (p) => JSON.stringify([p.texte, [...p.contenu.choix].sort()]);

const genVocabulaireQcm = () => {
    let payload = construireVocabulaireQcm();
    for (let tries = 0; tries < 12 && signatureVocab(payload) === derniereSignatureVocab; tries++) {
        payload = construireVocabulaireQcm();
    }
    derniereSignatureVocab = signatureVocab(payload);
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    definition_qcm:        genDefinitionQcm,
    expressions_relier:    genExpressionsRelier,
    proprietes_vf:         genProprietesVF,
    prix_unitaire_saisie:  genPrixUnitaireSaisie,
    interpretation_qcm:    genInterpretationQcm,
    vocabulaire_qcm:       genVocabulaireQcm,
};

export const CATEGORIES = [
    {
        id: 'definition_proportionnalite',
        label: 'Définition de la proportionnalité',
        formatsList: [
            { key: 'definition_qcm', label: 'QCM' },
            { key: 'proprietes_vf',  label: 'V/F' },
        ],
    },
    {
        id: 'expressions_proportionnalite_vie_courante',
        label: 'Expressions de la vie courante (prix au kilo, vitesse…)',
        formatsList: [
            { key: 'expressions_relier',   label: 'Relier' },
            { key: 'prix_unitaire_saisie', label: 'Saisie' },
            { key: 'interpretation_qcm',   label: 'QCM' },
            { key: 'vocabulaire_qcm',      label: 'QCM vocabulaire' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    FRUITS,
    PRIX_POOL,
    MASSE_POOL,
    DEBIT_POOL,
    DUREE_MIN_POOL,
    CONSO_POOL,
    K_POOL,
    SALAIRE_POOL,
    HEURES_POOL,
    VITESSE_POOL,
    DEF_VARIANTS,
    EXPRESSIONS_POOL,
    BUILDERS_V,
    BUILDERS_F,
    PROP_EXPRESSIONS,
    NON_PROP_EXPRESSIONS,
    fmt,
    genDefinitionQcm,
    genExpressionsRelier,
    genProprietesVF,
    genPrixUnitaireSaisie,
    genInterpretationQcm,
    genVocabulaireQcm,
};
